import { useQueryClient } from '@tanstack/react-query';
import { useStore, useStoreActions } from '~/contexts';
import { useMutationState } from '~/hooks/reactQuery/useMutationState';
import { selectors } from '~/store/state-selectors';
import { hasPoolWrite, planComponentEntries, rateWriteBody, taxPresetFromRateDTO } from '~/utils/salesTax';
import { QueryKeys } from '../../queryKeys';
export const useUpdateSalesTaxConfig = ()=>{
    const queryClient = useQueryClient();
    const { api } = useStoreActions();
    const businessId = useStore(selectors.user.businessId);
    const resolveComponentEntries = async (vendorId, preset)=>{
        const pool = await api.vendor.getSalesTaxComponents(vendorId);
        return planComponentEntries({
            components: preset.components,
            pool
        });
    };
    const refreshPoolAfterWrite = async (entries)=>{
        if (hasPoolWrite(entries)) {
            await queryClient.invalidateQueries({
                queryKey: [
                    QueryKeys.SalesTaxComponents
                ]
            });
        }
    };
    const { mutateAsync, isPending } = useMutationState({
        mutationFn: async (vars)=>{
            if (businessId == null) {
                return undefined;
            }
            if (vars.kind === 'settings') {
                await api.vendor.updateSalesTaxSettings(businessId, vars.settings);
                return undefined;
            }
            if (vars.kind === 'createRate') {
                const entries = await resolveComponentEntries(businessId, vars.preset);
                const created = await api.vendor.createSalesTaxRate(businessId, rateWriteBody(vars.preset, entries));
                await refreshPoolAfterWrite(entries);
                return taxPresetFromRateDTO(created);
            }
            const entries = await resolveComponentEntries(businessId, vars.next);
            const updated = await api.vendor.updateSalesTaxRate(businessId, vars.next.id, rateWriteBody(vars.next, entries));
            await refreshPoolAfterWrite(entries);
            return taxPresetFromRateDTO(updated);
        },
        onSuccess: (_preset, vars)=>{
            if (vars.kind !== 'settings') {
                void queryClient.invalidateQueries({
                    queryKey: [
                        QueryKeys.SalesTaxConfig
                    ]
                });
            }
        }
    });
    return {
        updateSalesTaxConfig: mutateAsync,
        isLoading: isPending
    };
};
